import { Bell, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserNav } from "@/components/student-dashboard/UserNav";
import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR, es, fr } from "date-fns/locale";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { MobileMenuToggle } from "@/components/dashboard/header/MobileMenuToggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

export const StudentDashboardHeader = () => {
  const { profile } = useAuth();
  const isMobile = useIsMobile();
  const { t } = useTranslations("student");
  const { language } = useLanguage();
  
  // Select the appropriate date locale based on the current language
  const getDateLocale = () => {
    switch(language) {
      case 'pt': return ptBR;
      case 'es': return es;
      case 'fr': return fr;
      default: return undefined; // Default English locale
    }
  };
  
  const dateLocale = getDateLocale();
  
  const { data: notifications } = useQuery({
    queryKey: ["student-notifications", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return {
        approvedRequests: [],
        scheduledClasses: []
      };
      
      const [approvedRequests, scheduledClasses] = await Promise.all([
        supabase.from("schedule_requests").select(`
            id,
            created_at,
            requested_date,
            trainer_profile:profiles!trainer_id(
              first_name,
              last_name
            )
          `).eq("student_id", profile.id).eq("status", "approved").order('created_at', {
          ascending: false
        }).limit(5),
        
        supabase.from("schedule_events").select(`
            id,
            start_time,
            trainer_profile:profiles!trainer_id(
              first_name,
              last_name
            )
          `).eq("student_id", profile.id).eq("status", "scheduled").gt("start_time", new Date().toISOString()).order('start_time', {
          ascending: true
        }).limit(5)
      ]);
      
      return {
        approvedRequests: approvedRequests.data || [],
        scheduledClasses: scheduledClasses.data || []
      };
    },
    enabled: !!profile?.id,
    refetchInterval: 30000
  });

  return (
    <header className="fixed top-2 left-2 right-2 h-16 bg-white/90 dark:bg-gray-800/95 backdrop-blur-xl border border-slate-100/50 dark:border-gray-700/50 z-60 rounded-xl shadow-lg transition-all duration-300 mx-2">
      <div className="flex items-center justify-between px-4 h-full max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4">
          {isMobile && (
            <MobileMenuToggle />
          )}
          <div className="flex items-center gap-2">
            <img 
              src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" 
              alt="Trainerfolio Logo" 
              className={`${isMobile ? "h-10 w-auto" : "h-14 w-auto"}`}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative hover:bg-flame/5 rounded-xl">
                <Bell className="h-5 w-5 text-flame" />
                {notifications && notifications.approvedRequests.length + notifications.scheduledClasses.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 text-xs flex items-center justify-center bg-red-500 text-white rounded-full shadow-sm animate-pulse">
                    {notifications.approvedRequests.length + notifications.scheduledClasses.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 rounded-xl shadow-lg border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 backdrop-blur-xl dropdown-opaque z-[100]" align="end">
              <div className="p-4 space-y-4 max-h-96 overflow-auto">
                <h3 className="text-sm font-medium text-slate-800 dark:text-slate-200 border-b pb-2 dark:border-gray-700">
                  {t("header.notifications.title")}
                </h3>
                
                {!notifications || notifications.approvedRequests.length + notifications.scheduledClasses.length === 0 ? (
                  <p className="text-sm text-muted-foreground dark:text-gray-400 text-center py-4">
                    {t("header.notifications.noNotifications")}
                  </p>
                ) : (
                  <>
                    {notifications.approvedRequests.map(request => (
                      <div key={request.id} className="flex items-start gap-2 text-sm border-b border-gray-100 dark:border-gray-700 pb-3 group hover:bg-slate-50 dark:hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                        <Calendar className="h-4 w-4 text-flame mt-1 flex-shrink-0" />
                        <div>
                          <p className="dark:text-gray-200">{t("header.notifications.approvedClass")} <span className="font-medium">{request.trainer_profile.first_name} {request.trainer_profile.last_name}</span></p>
                          <p className="text-xs text-muted-foreground dark:text-gray-400">
                            {format(new Date(request.requested_date), "dd/MM/yyyy", {
                              locale: dateLocale
                            })} {t("header.notifications.at")} {format(new Date(request.requested_date), "HH:mm", {
                              locale: dateLocale
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {notifications.scheduledClasses.map(event => (
                      <div key={event.id} className="flex items-start gap-2 text-sm border-b border-gray-100 dark:border-gray-700 pb-3 group hover:bg-slate-50 dark:hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                        <Calendar className="h-4 w-4 text-flame mt-1 flex-shrink-0" />
                        <div>
                          <p className="dark:text-gray-200">{t("header.notifications.upcomingClass")} <span className="font-medium">{event.trainer_profile.first_name} {event.trainer_profile.last_name}</span></p>
                          <p className="text-xs text-muted-foreground dark:text-gray-400">
                            {format(new Date(event.start_time), "dd/MM/yyyy", {
                              locale: dateLocale
                            })} {t("header.notifications.at")} {format(new Date(event.start_time), "HH:mm", {
                              locale: dateLocale
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <UserNav />
        </div>
      </div>
    </header>
  );
};
